import { cn } from '@/utils/styles';
import { Skeleton } from '@design-system/Skeleton';
import { HomeJourneyCard } from '@screens/HomePage/components/HomeJourneyCard';
import { useGetJourneyState } from '@viome/viome-base/src/usecase/Home/HomeQuery';

interface HomeActivateJourneyProps {
  screen: 'nornr' | 'retest';
  screenType?: string;
}

export const HomeActivateJourney = ({
  screen,
  screenType = 'nornr',
}: HomeActivateJourneyProps) => {
  const { data: journeyStateData, isLoading, isError } = useGetJourneyState();
  const isRetestScreen = screen === 'retest';

  const steps = [
    {
      id: '1',
      label: 'Register Kit',
      description:
        "Connect your kit to your Viome account, so we know it's you.",
      completed: journeyStateData?.kit_received || false,
    },
    {
      id: '2',
      label: 'Collect Sample(s)',
      description: 'Please record the dates you collect the samples.',
      completed: journeyStateData?.samples_collected || false,
    },
    {
      id: '3',
      label: 'Complete Questionnaire',
      description:
        'Incomplete questions will delay your results and recommendations.',
      completed: journeyStateData?.is_questionaire_completed || false,
    },
  ];

  const allStepsCompleted =
    (journeyStateData?.kit_received ?? false) &&
    (journeyStateData?.samples_collected ?? false) &&
    (journeyStateData?.is_questionaire_completed ?? false);

  const containerHeading =
    screenType === 'nornr'
      ? allStepsCompleted
        ? `All set! You've completed every step.`
        : 'Activate Your Journey'
      : allStepsCompleted
        ? `All set! You've completed every step.`
        : 'Get started with retesting in a few quick steps';

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton
          className={cn('h-8 w-48', isRetestScreen && 'rounded-lg bg-white/10')}
        />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              className={cn(
                'h-32 w-full',
                isRetestScreen && 'rounded-lg bg-white/10',
              )}
            />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !journeyStateData) {
    return;
  }

  return (
    <div className={cn('space-y-4', isRetestScreen && 'text-secondary')}>
      <h2
        className={cn(
          'typography-h2 mt-8',
          !isRetestScreen && 'text-primary',
          isRetestScreen && 'text-[#C8CFDBCC]',
        )}
      >
        {containerHeading}
      </h2>
      <div className="grid gap-4">
        {steps.map((step) => (
          <HomeJourneyCard
            key={step.id}
            label={step.label}
            description={step.completed ? 'Completed' : step.description}
            completed={step.completed}
            showProgress={step.id === '3'}
            progressValue={
              step.id === '3'
                ? journeyStateData?.questionaire_completion_percentage || 0
                : 0
            }
            showContinue={!step.completed}
            isRetest={isRetestScreen}
            stepId={step.id}
          />
        ))}
      </div>
    </div>
  );
};
